import React, { useRef, useState, useEffect } from 'react';
import { Container, MapContainer, Lens, FullscreenOverlay } from './styles';
import SubTitle from 'common/SubTitle/SubTitle';

interface ICityMapProps {
  image: string;
}

const CityMap: React.FC<ICityMapProps> = ({ image }) => {
  const imgRef = useRef<HTMLImageElement>(null);
  const lensRef = useRef<HTMLDivElement>(null);

  const [isMobile, setIsMobile] = useState(false);
  const [showLens, setShowLens] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [lensPosition, setLensPosition] = useState({ x: 0, y: 0 });
  const [backgroundPosition, setBackgroundPosition] = useState('0px 0px');
  const [backgroundSize, setBackgroundSize] = useState('0px 0px');

  const zoom = 3;

  useEffect(() => {
    const updateBackgroundSize = () => {
      const img = imgRef.current;
      if (!img || isMobile) return;

      const width = img.offsetWidth;
      const height = img.offsetHeight;

      if (width > 0 && height > 0) {
        const bgWidth = width * zoom;
        const bgHeight = height * zoom;
        setBackgroundSize(`${bgWidth}px ${bgHeight}px`);
      }
    };

    updateBackgroundSize();

    const interval = setInterval(() => {
      const img = imgRef.current;
      if (img?.offsetWidth) {
        updateBackgroundSize();
        clearInterval(interval);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [image, isMobile]);

  const handleImageLoad = () => {
    const img = imgRef.current;
    if (img && !isMobile) {
      const bgWidth = img.offsetWidth * zoom;
      const bgHeight = img.offsetHeight * zoom;
      setBackgroundSize(`${bgWidth}px ${bgHeight}px`);
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isMobile) return;

    const img = imgRef.current;
    const lens = lensRef.current;
    if (!img || !lens) return;

    const rect = img.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const lensWidth = lens.offsetWidth / 2;
    const lensHeight = lens.offsetHeight / 2;

    const posX = Math.max(0, Math.min(x - lensWidth, img.offsetWidth - lens.offsetWidth));
    const posY = Math.max(0, Math.min(y - lensHeight, img.offsetHeight - lens.offsetHeight));

    setLensPosition({ x: posX, y: posY });

    setBackgroundPosition(`-${x * zoom - lensWidth * zoom}px -${y * zoom - lensHeight * zoom}px`);
  };

  const handleMobileClick = () => {
    if (window.innerWidth <= 768) {
      setFullscreen(true);
    }
  };

  const handleOverlayClose = () => {
    setFullscreen(false);
  };

  return (
    <Container>
      <SubTitle text="Mapa Turístico" />
      <MapContainer
        onMouseMove={handleMouseMove}
        onMouseEnter={() => !isMobile && setShowLens(true)}
        onMouseLeave={() => !isMobile && setShowLens(false)}
        onClick={handleMobileClick}>
        <img ref={imgRef} src={image} alt="Mapa turístico" width="100%" height="auto" onLoad={handleImageLoad} />
        {!isMobile && showLens && (
          <Lens
            ref={lensRef}
            style={{
              left: `${lensPosition.x}px`,
              top: `${lensPosition.y}px`,
              backgroundImage: `url("${encodeURI(image)}")`,
              backgroundPosition,
              backgroundSize,
            }}
          />
        )}
      </MapContainer>

      {fullscreen && (
        <FullscreenOverlay onClick={handleOverlayClose}>
          <img src={image} alt="Zoom mobile" draggable={false} />
        </FullscreenOverlay>
      )}
    </Container>
  );
};

export default CityMap;
